<template>
  <DrawerModal>
    <section id="warranty-request" class="h-full">
      <div
        class="flex flex-col h-full font-light pb-14 md:pb-16 font-ff-condensed"
      >
        <!-- Title section -->
        <div class="md:px-[24px] z-[10000] mb-6">
          <h2
            class="uppercase font-bold italic text-2xl font-ff-good-pro-cond text-[#000] mt-0 md:mt-4 mb-0"
          >
            {{ $t("common.warranty_request") }}
          </h2>
          <p
            class="text-black font-normal text-sm leading-[20px] font-ff-good-pro-cond"
          >
            {{ $t("common.having_trouble_fill_in_form") }}
            <nuxt-link
              class="underline"
              :to="$i18nPath(store.pageLinks['contact-us'])"
              @click="closeModal"
            >
              {{ $t("common.contact_support") }}
            </nuxt-link>
          </p>
        </div>

        <!-- Form -->
        <div class="md:px-[24px] overflow-y-scroll no-scrollbar flex-1 md:mb-0">
          <form method="POST" @submit.prevent="onSubmit">
            <!-- QR Code Number -->
            <div class="mb-6 space-y-1">
              <label
                for="qr_code"
                class="gap-1.5 flex items-center text-[#373737] text-sm font-ff-good-pro-cond uppercase tracking-wider font-bold"
              >
                {{ $t("common.qr_code_number") }}
                <RequiredMark has-bottom>
                  <span
                    class="text-lg font-normal capitalize font-ff-good-pro-cond"
                    >{{ $t("common.is_required") }}</span
                  >
                </RequiredMark>
              </label>
              <input
                v-model="form.qr_code_number"
                id="qr_code_number"
                type="number"
                tabindex="1"
                :class="[
                  { 'is-invalid': errorsObj && errorsObj.qr_code_number },
                  'text-black block w-full px-4 py-3 text-lg rounded-md border-[#DBDBDB] font-ff-good-pro-cond focus:border-primary focus:ring-0  placeholder:text-black placeholder:text-opacity-60 placeholder:text-lg',
                ]"
                @input="clearValidation('qr_code_number')"
                :placeholder="$t('common.placeholder')"
                required
              />
              <p
                v-if="errorsObj && errorsObj.qr_code_number && hasDCRError"
                class="mt-1 text-sm italic font-medium text-red-500"
                v-text="$t('common.dcr_not_found')"
              />
              <p
                v-else-if="errorsObj && errorsObj.qr_code_number"
                class="mt-1 text-sm italic font-medium text-red-500"
                v-text="errorsObj.qr_code_number[0]"
              />
            </div>

            <!-- First Name -->
            <div class="mb-6 space-y-1">
              <label
                for="first_name"
                class="gap-1.5 flex items-center text-[#373737] text-sm font-ff-good-pro-cond uppercase tracking-wider font-bold"
              >
                {{ $t("common.first_name") }}
                <RequiredMark>
                  <span
                    class="text-lg font-normal capitalize font-ff-good-pro-cond"
                    >{{ $t("common.is_required") }}</span
                  >
                </RequiredMark>
              </label>
              <input
                v-model="form.first_name"
                id="first_name"
                type="text"
                tabindex="2"
                :class="[
                  { 'is-invalid': errorsObj && errorsObj.first_name },
                  'text-black block w-full px-4 py-3 text-lg rounded-md border-[#DBDBDB] font-ff-good-pro-cond focus:border-primary focus:ring-0  placeholder:text-black placeholder:text-opacity-60 placeholder:text-lg',
                ]"
                @input="clearValidation('first_name')"
                :placeholder="$t('common.placeholder')"
                required
              />
              <p
                v-if="errorsObj && errorsObj.first_name"
                class="mt-1 text-sm italic font-medium text-red-500"
                v-text="errorsObj.first_name[0]"
              />
            </div>

            <!-- Last Name -->
            <div class="mb-6 space-y-1">
              <label
                for="last_name"
                class="gap-1.5 flex items-center text-[#373737] text-sm font-ff-good-pro-cond uppercase tracking-wider font-bold"
              >
                {{ $t("common.last_name") }}
                <RequiredMark>
                  <span
                    class="text-lg font-normal capitalize font-ff-good-pro-cond"
                    >{{ $t("common.is_required") }}</span
                  >
                </RequiredMark>
              </label>
              <input
                v-model="form.last_name"
                id="last_name"
                type="text"
                tabindex="3"
                :class="[
                  { 'is-invalid': errorsObj && errorsObj.last_name },
                  'text-black block w-full px-4 py-3 text-lg  rounded-md border-[#DBDBDB] font-ff-good-pro-cond focus:border-primary focus:ring-0  placeholder:text-black placeholder:text-opacity-60 placeholder:text-lg',
                ]"
                @input="clearValidation('last_name')"
                :placeholder="$t('common.placeholder')"
                required
              />
              <p
                v-if="errorsObj && errorsObj.last_name"
                class="mt-1 text-sm italic font-medium text-red-500"
                v-text="errorsObj.last_name[0]"
              />
            </div>

            <!-- Email -->
            <div class="mb-6 space-y-1">
              <label
                for="email"
                class="gap-1.5 flex items-center text-[#373737] text-sm font-ff-good-pro-cond uppercase tracking-wider font-bold"
              >
                {{ $t("common.email") }}
                <RequiredMark>
                  <span
                    class="text-lg font-normal capitalize font-ff-good-pro-cond"
                    >{{ $t("common.is_required") }}</span
                  >
                </RequiredMark>
              </label>
              <input
                v-model="form.email"
                id="email"
                type="email"
                tabindex="4"
                :class="[
                  { 'is-invalid': errorsObj && errorsObj.email },
                  'text-black block w-full px-4 py-3 text-lg rounded-md border-[#DBDBDB] font-ff-good-pro-cond focus:border-primary focus:ring-0  placeholder:text-black placeholder:text-opacity-60 placeholder:text-lg',
                ]"
                @input="clearValidation('email')"
                :placeholder="$t('common.placeholder')"
                required
              />
              <p
                v-if="errorsObj && errorsObj.email"
                class="mt-1 text-sm italic font-medium text-red-500"
                v-text="errorsObj.email[0]"
              />
            </div>

            <!-- Country/Region -->
            <div v-if="store.country == 'int'" class="mb-6 space-y-1">
              <!-- Country -->
              <div class="w-full">
                <label
                  for="country"
                  class="gap-1.5 flex items-center text-[#373737] text-sm font-ff-good-pro-cond uppercase tracking-wider font-bold"
                >
                  {{ $t("common.country_region") }}
                  <RequiredMark>
                    <span
                      class="text-lg font-normal capitalize font-ff-good-pro-cond"
                      >{{ $t("common.is_required") }}</span
                    >
                  </RequiredMark>
                </label>

                <div class="relative mt-1 rounded-md">
                  <div class="mt-1">
                    <Selector
                      tabindex="5"
                      :options="localeStore.countries"
                      name="country"
                      v-model="form.country"
                      :title="$t('common.country')"
                      unique-key="id"
                      label="name"
                      value-field="id"
                      input-text-class="text-black"
                      placeholder-class="text-lg text-black text-opacity-60"
                      container-class="border-[#DBDBDB] font-ff-good-pro-cond focus:border-primary focus:ring-0"
                      :errorMessage="
                        errorsObj && errorsObj.country && errorsObj.country[0]
                      "
                      :has-error="!!(errorsObj && errorsObj.country)"
                      @input="clearValidation('country')"
                    >
                      <template #display-name="{ selected }">
                        <div class="flex items-center text">
                          <span class="text-2xl text-gray-500">
                            {{ selected.flag }}
                          </span>
                          <span class="ml-2 text-base">{{
                            selected.name
                          }}</span>
                        </div>
                      </template>
                      <template #option="{ option }">
                        <div class="flex items-center text">
                          <span class="text-2xl text-gray-500">
                            {{ option.flag }}
                          </span>
                          <span class="ml-2 text-base">{{ option.name }}</span>
                        </div>
                      </template>
                    </Selector>
                  </div>
                </div>
              </div>
            </div>

            <!-- Faulty Part -->
            <div class="mb-6 space-y-1" v-if="warrantyStore.faultyParts.length">
              <label
                for="faulty_part"
                class="gap-1.5 flex items-center text-[#373737] text-sm font-ff-good-pro-cond uppercase tracking-wider font-bold"
              >
                {{ $t("common.faulty_part") }}
                <RequiredMark>
                  <span
                    class="text-lg font-normal capitalize font-ff-good-pro-cond"
                    >{{ $t("common.is_required") }}</span
                  >
                </RequiredMark>
              </label>
              <Selector
                :options="warrantyStore.faultyParts"
                name="faulty_part"
                tabindex="6"
                value-field="slug"
                v-model="form.faulty_part"
                :title="$t('common.faulty_part')"
                unique-key="slug"
                :placeholder="`${$t('common.select_here')}...`"
                label="name"
                :has-clear="false"
                input-text-class="text-black"
                placeholder-class="text-lg text-black text-opacity-60"
                container-class="border-[#DBDBDB] font-ff-good-pro-cond focus:border-primary focus:ring-0"
                max-width="max-w-[122px] xl:min-w-[122px]"
                @input="clearValidation('faulty_part')"
                :has-error="!!(errorsObj && errorsObj.faulty_part)"
              >
                <template #option="{ option }">
                  <div class="flex items-center text">
                    <span class="ml-2 text-base">{{ option.name }}</span>
                  </div>
                </template>
              </Selector>
              <p
                v-if="errorsObj && errorsObj.faulty_part"
                class="mt-1 text-sm italic font-medium text-red-500"
                v-text="errorsObj.faulty_part[0]"
              />
            </div>

            <!-- Problem Description -->
            <div class="mb-6 space-y-1">
              <label
                for="problem_description"
                class="gap-1.5 flex items-center text-[#373737] text-sm font-ff-good-pro-cond uppercase tracking-wider font-bold"
              >
                {{ $t("common.problem_description") }}
                <RequiredMark>
                  <span
                    class="text-lg font-normal capitalize font-ff-good-pro-cond"
                    >{{ $t("common.is_required") }}</span
                  >
                </RequiredMark>
              </label>
              <textarea
                v-model="form.problem_description"
                id="problem_description"
                rows="4"
                tabindex="7"
                maxlength="1000"
                :placeholder="$t('common.placeholder')"
                :class="[
                  { 'is-invalid': errorsObj && errorsObj.problem_description },
                  'text-black block w-full px-4 py-3 text-lg rounded-md border-[#DBDBDB] font-ff-good-pro-cond focus:border-primary focus:ring-0  placeholder:text-black placeholder:text-opacity-60 placeholder:text-lg',
                ]"
                @input="clearValidation('problem_description')"
              ></textarea>
              <div class="flex justify-end w-full">
                <span>{{ form.problem_description.length }}/1000</span>
              </div>
              <p
                v-if="errorsObj && errorsObj.problem_description"
                class="mt-1 text-sm italic font-medium text-red-500"
                v-text="errorsObj.problem_description[0]"
              />
            </div>

            <div class="mb-6">
              <label
                for="graphic-evidence"
                class="gap-1.5 flex items-center text-[#373737] text-sm font-ff-good-pro-cond uppercase tracking-wider font-bold"
              >
                {{ $t("common.graphic_evidence") }}
                <RequiredMark>
                  <span
                    class="text-lg font-normal capitalize font-ff-good-pro-cond"
                    >{{ $t("common.is_required") }}</span
                  >
                </RequiredMark>
              </label>

              <!-- Custom File Input -->
              <span
                class="mt-2.5 inline-block text-base font-normal text-black text-opacity-60 font-ff-good-pro-cond"
                v-text="$t('common.upload_problem_images_or_videos')"
              />
              <div class="mt-[23px]">
                <input
                  ref="fileInput"
                  type="file"
                  accept=".mp4, .WMV, .AVI, .MKV, .WEBM, .png, .jpeg, .jpg, .webp .pdf"
                  class="hidden text-white cursor-pointers"
                  @change="parseUpload"
                />

                <div
                  class="flex gap-[12px] font-ff-good-pro-cond font-medium items-center"
                >
                  <button
                    type="button"
                    class="text-sm md:text-base leading-6 bg-primary-500 rounded-[3px] text-white px-[14px] py-[3px]"
                    @click="openFilePicker"
                  >
                    {{ $t("common.choose_file") }}
                  </button>
                  <span v-if="form.graphic_evidence" class="text-black">
                    {{ form.graphic_evidence && form.graphic_evidence.name }}
                  </span>
                  <span
                    v-else
                    class="text-base font-normal text-black text-opacity-60 font-ff-good-pro-cond"
                  >
                    {{ $t("common.no_file_choose") }}
                  </span>
                </div>
                <p
                  v-if="errorsObj && errorsObj.graphic_evidence"
                  class="mt-1 text-sm italic font-medium text-red-500"
                  v-text="errorsObj.graphic_evidence[0]"
                />
              </div>
            </div>

            <hr class="border-[#DBDBDB] my-[30px]" />

            <div class="mb-6">
              <label
                for="graphic-evidence"
                class="gap-1.5 flex items-center text-[#373737] text-sm font-ff-good-pro-cond uppercase tracking-wider font-bold"
              >
                {{ $t("common.purchase_invoice") }}
                <RequiredMark>
                  <span
                    class="text-lg font-normal capitalize font-ff-good-pro-cond"
                    >{{ $t("common.is_required") }}</span
                  >
                </RequiredMark>
              </label>

              <!-- Custom File Input -->
              <div
                class="mt-2.5 flex items-center gap-[28px] font-ff-good-pro-cond"
              >
                <span
                  class="text-base text-black cursor-pointer"
                  @click="setTab('upload')"
                  :class="
                    !hasTab('upload')
                      ? 'text-opacity-60'
                      : 'font-medium border-b-2 border-black'
                  "
                >
                  {{ $t("common.upload") }}
                </span>
                <span
                  class="text-base text-black cursor-pointer"
                  @click="setTab('enter_invoice_number')"
                  :class="
                    !hasTab('enter_invoice_number')
                      ? 'text-opacity-60'
                      : 'font-medium border-b-2 border-black'
                  "
                >
                  {{ $t("common.enter_invoice_number") }}
                </span>
              </div>
              <div
                class="mt-[23px] h-[58px] flex flex-col"
                v-if="currentTab == 'upload'"
              >
                <input
                  ref="fileInputInvoice"
                  type="file"
                  accept=".doc, .docx, .pdf"
                  class="hidden text-white cursor-pointers"
                  @change="invoicParseUpload"
                />

                <div
                  class="flex gap-[12px] font-ff-good-pro-cond font-medium items-center"
                >
                  <button
                    type="button"
                    class="text-sm md:text-base leading-6 bg-primary-500 rounded-[3px] text-white px-[14px] py-[3px]"
                    @click="openInvoiceFilePicker"
                  >
                    {{ $t("common.choose_file") }}
                  </button>
                  <span v-if="form.purchase_invoice" class="text-black">
                    {{ form.purchase_invoice && form.purchase_invoice.name }}
                  </span>
                  <span
                    v-else
                    class="text-base font-normal text-black text-opacity-60 font-ff-good-pro-cond"
                  >
                    {{ $t("common.no_file_choose") }}
                  </span>
                </div>
                <p
                  v-if="errorsObj && errorsObj.purchase_invoice"
                  class="mt-1 text-sm italic font-medium text-red-500"
                  v-text="errorsObj.purchase_invoice[0]"
                />
              </div>
              <div class="mt-[23px]" v-else>
                <input
                  v-model="form.invoice_number"
                  id="invoice_number"
                  type="text"
                  :class="[
                    { 'is-invalid': errorsObj && errorsObj.invoice_number },
                    'text-black block w-full px-4 py-3 text-lg rounded-md border-[#DBDBDB] font-ff-good-pro-cond focus:border-primary focus:ring-0  placeholder:text-black placeholder:text-opacity-60 placeholder:text-lg',
                  ]"
                  @input="clearValidation('invoice_number')"
                  :placeholder="$t('common.placeholder')"
                  required
                />
                <p
                  v-if="errorsObj && errorsObj.invoice_number"
                  class="mt-1 text-sm italic font-medium text-red-500"
                  v-text="errorsObj.invoice_number[0]"
                />
              </div>
            </div>

            <!-- I Agree Checkbox -->
            <div class="mb-6 space-y-1">
              <label
                class="flex justify-start items-center border border-transparent cursor-pointer select-none pe-2 mt-[20px] md:mt-[9px] md:block text-white warranty__policies--checkbox"
                :class="{
                  'border-red-500 bg-red-50 text-red-400':
                    errorsObj && errorsObj.accept_terms,
                }"
              >
                <input
                  id="warranty_tos"
                  v-model="accept_terms"
                  type="checkbox"
                  class="me-3 cursor-pointer mt-2.5 md:mt-0 bg-white relative bottom-[.1rem] font-ff-good-pro-cond text-primary-600 rounded-[4px] border-primary-600 focus:ring-primary-600 focus:ring-2"
                  data-cons-preference="terms-and-conditions"
                  @change="clearValidation('accept_terms')"
                />
                <!-- eslint-disable vue/no-v-html -->
                <span
                  class="text-base font-normal !leading-6 md:!leading-6 font-ff-good-pro-cond text-black terms warranty__checkmark"
                  v-html="
                    $t('common.i_agree_with_the_terms_and_conditions').replace(
                      'legal',
                      $i18nPath(store.pageLinks['terms'])
                    )
                  "
                />
                <!-- eslint-enable -->
              </label>
              <p
                v-if="errorsObj && errorsObj.accept_terms"
                class="mt-1 text-sm italic font-medium text-red-500"
                v-text="errorsObj.accept_terms[0]"
              />
            </div>
            <div class="hidden">
              <vue-hcaptcha
                ref="invisibleHcaptcha"
                size="invisible"
                :sitekey="$config.HCAPTCHA_SITE_KEY"
                theme="light"
                :language="store.locale"
                @verify="onVerify"
                @expired="onExpire"
                @challengeExpired="onExpire"
                @closed="onExpire"
                @error="onError"
              />
            </div>
          </form>
        </div>

        <!-- Fixed Footer with Back and Apply Buttons -->
        <div
          class="absolute bottom-0 left-0 right-0 flex justify-between w-full pb-3 space-x-4 bg-white md:pb-1 md:px-6"
        >
          <Btn
            type="button"
            size="lg"
            block
            @click="onBack"
            :rounded="false"
            class="uppercase rounded-md"
            color="secondary"
            :disabled="isSending"
            v-text="$t('common.back')"
          />
          <Btn
            type="button"
            size="lg"
            block
            @click="onSubmit"
            :rounded="false"
            class="uppercase rounded-md"
            color="primary"
            :disabled="isSending || isEmpty"
          >
            <div class="flex items-center justify-center space-x-2">
              <Spinner v-if="isSending" class="text-white animate-spin" />
              <span
                class="text-lg font-bold font-ff-good-pro-cond text-white leading-[26px] uppercase"
              >
                {{ $t("common.apply") }}</span
              >
            </div>
          </Btn>
        </div>
      </div>
      <div
        v-if="hasDCRErrorModal"
        class="z-[50000] w-[442px] bg-white rounded-md border-2 border-primary shadow-lg flex flex-col items-center justify-center py-4 fixed top-[70px] right-[40px]"
      >
        <CloseIcon
          class="absolute inline w-6 h-6 text-black cursor-pointer right-2 top-2"
          @click="closeDCRError"
        />
        <DcrNotFoundIcon class="w-[442px]" />
        <span class="text-base italic text-black">
          {{ $t("common.dcr_not_found") }}
        </span>
      </div>
    </section>
  </DrawerModal>
</template>

<script setup>
import { ref, computed } from "vue";
import { useTranslation } from "i18next-vue";
import DrawerModal from "~/components/DrawerModal.vue"; // Update the path based on where the file is located
import WarrantySuccessModal from "~/modals/WarrantyRequestSuccess.vue";
import { useModal, useVfm } from "vue-final-modal";
import { useMainStore } from "~/store";
import { useLocaleStore } from "~/store/locales";
import VueHcaptcha from "@hcaptcha/vue3-hcaptcha";
import DcrNotFoundIcon from "~/assets/svg/dcr-not-found.svg?component";
import { useWarrantyStore } from "~/store/warranty";
const CloseIcon = defineAsyncComponent(() =>
  import("@/assets/svg/close.svg?component")
);

const { t } = useTranslation();
const vfm = useVfm();
const nuxtApp = useNuxtApp();
const store = useMainStore();
const localeStore = useLocaleStore();
const warrantyStore = useWarrantyStore();
const $config = nuxtApp.$config.public;

const formFields = reactive({
  problem_description: "",
  qr_code_number: "",
  faulty_part: "",
  last_name: "",
  first_name: "",
  email: "",
  accept_terms: false,
  graphic_evidence: "",
  purchase_invoice: "",
  invoice_number: "",
  country: "",
  company_id: "",
});
const $i18nPath = nuxtApp.$i18nPath;

const invisibleHcaptcha = ref(null);
let form = reactive(Object.assign({}, formFields));
const isSending = ref(false);
const hasDCRError = ref(false);
const hasDCRErrorModal = ref(false);
const errorsObj = ref({});
const fileInput = ref(null);
const accept_terms = ref(false);
const isEmpty = ref(true);
const currentTab = ref("upload");
const fileInputInvoice = ref(null);

function closeModal() {
  vfm.closeAll();
}

function closeDCRError() {
  hasDCRErrorModal.value = false;
}

function hasTab(tab) {
  return currentTab.value === tab;
}
function setTab(tab) {
  currentTab.value = tab;
  if (tab === "upload") {
    form.invoice_number = "";
  } else {
    form.purchase_invoice = "";
  }
}

function clearValidation(field) {
  if (errorsObj.value) {
    switch (field) {
      case "qr_code_number":
        errorsObj.value.qr_code_number = null;
        break;
      case "last_name":
        errorsObj.value.last_name = null;
        break;
      case "first_name":
        errorsObj.value.first_name = null;
        break;
      case "accept_terms":
        errorsObj.value.accept_terms = null;
        break;
      case "problem_description":
        errorsObj.value.problem_description = null;
        break;
      case "email":
        errorsObj.value.email = null;
        break;
      case "country":
        errorsObj.value.country = null;
        break;
      case "graphic_evidence":
        errorsObj.value.graphic_evidence = null;
        break;
      case "purchase_invoice":
        errorsObj.value.purchase_invoice = null;
        break;
      case "invoice_number":
        errorsObj.value.invoice_number = null;
        break;
      case "faulty_part":
        errorsObj.value.faulty_part = null;
        break;

      default:
        break;
    }
  }
  let count = 0;
  for (const key in form) {
    if (form[key]) {
      isEmpty.value = false;
    } else {
      count++;
    }
  }
  if (count >= Object.keys(form).length && !accept_terms.value) {
    isEmpty.value = true;
  } else if (accept_terms.value) {
    isEmpty.value = false;
  }
}

function parseUpload(event) {
  if (!event.target.files) {
    return;
  }
  form.graphic_evidence = event.target.files[0];
  clearValidation("graphic_evidence");
}

function openFilePicker() {
  // Trigger the click event of the file input

  if (fileInput.value) {
    fileInput.value.click();
  }
}
function invoicParseUpload(event) {
  if (!event.target.files) {
    return;
  }
  form.purchase_invoice = event.target.files[0];
  clearValidation("purchase_invoice");
}

function openInvoiceFilePicker() {
  // Trigger the click event of the file input
  if (fileInputInvoice.value) {
    fileInputInvoice.value.click();
  }
}

const countries = [
  { label: "United States", value: "US" },
  { label: "Canada", value: "CA" },
  { label: "Mexico", value: "MX" },
];

const hasErrors = computed(() => {
  return Object.keys(errorsObj.value).length > 0;
});

function openWarrantySuccessModal() {
  const { open, close } = useModal({
    component: WarrantySuccessModal,
    attrs: {},
  });
  open();
}

function onVerify(token, ekey) {
  const defaultCountry = localeStore.defaultLocales.find(
    (_v) => _v.slug === store.country
  );
  form.company_id = defaultCountry ? defaultCountry.id : "";

  if (store.country != "int") {
    form.country = store.country;
  }
  const formData = new FormData();
  formData.append("country", form.country);
  formData.append("invoice_number", form.invoice_number);
  formData.append("purchase_invoice", form.purchase_invoice);
  formData.append("problem_description", form.problem_description);
  formData.append("company_id", form.company_id);
  formData.append("qr_code_number", form.qr_code_number);
  formData.append("faulty_part", form.faulty_part);
  formData.append("graphic_evidence", form.graphic_evidence);
  formData.append("email", form.email);
  // formData.append('subject', form.subject)
  formData.append("last_name", form.last_name);
  formData.append("first_name", form.first_name);
  formData.append("accept_terms", accept_terms.value);
  formData.append("h-captcha-response", token);

  nuxtApp
    .$api("/api/front/warranty-inquiry", {
      method: "POST",
      body: formData,
    })
    .then((response) => {
      console.log(response);
      if (response.error) {
        isSending.value = false;
      } else {
        form = Object.assign({}, formFields);
        form.country = "";

        isSending.value = false;
        isEmpty.value = true;
        openWarrantySuccessModal();

        window._iub.cons_instructions.push([
          "submit",
          {
            writeOnLocalStorage: false,
            form: {
              selector: document.getElementById("warranty_request_form"),
            },
            consent: {
              legal_notices: [
                {
                  identifier: "privacy_policy",
                },
                {
                  identifier: "cookie_policy",
                },
                {
                  identifier: "term",
                },
              ],
            },
          },
          {
            success(response) {
              isSending.value = false;
              // console.log(response)
            },
            error(response) {
              isSending.value = false;
              console.warning(response);
            },
          },
        ]);
      }
    })
    .catch((error) => {
      console.error(error);

      //  nuxtApp.$toast.error(response.error.value.cause._data.message);
      if (error._data) {
        const dcrErr = error._data.errors.qr_code_number;
        if (dcrErr && dcrErr[0] && dcrErr[0] == "validation.dcr_not_found") {
          hasDCRError.value = true;
          hasDCRErrorModal.value = true;
        }
        errorsObj.value = error._data.errors;
        nuxtApp.$toast.error(error._data.message);
      } else if (error && !error._data) {
        nuxtApp.$toast.error($t("common.server_error_try_again"));
      }

      isSending.value = false;
    });
}

function onExpire() {
  nuxtApp.$toast.error("Captcha expired");
  isSending.value = false;
}

function onError() {
  isSending.value = false;
}

function onSubmit() {
  if (isSending.value) {
    return;
  }

  isSending.value = true;
  errorsObj.value = {};

  if (invisibleHcaptcha.value) {
    invisibleHcaptcha.value.execute();
  }
}
function onBack() {
  vfm.closeAll();
}
</script>
<style lang="postcss">
.required-star {
  position: relative;
  cursor: pointer;
}

/* Show tooltip on hover */
.required-star:hover .tooltip {
  display: block;
}
input::placeholder {
  font-size: 14px; /* Adjust the font size to your preferred size */
  color: #9e9e9e; /* Optional: You can adjust the color of the placeholder text as well */
}

textarea::placeholder {
  font-size: 14px; /* Adjust the font size to your preferred size */
  color: #9e9e9e; /* Optional: Adjust the placeholder color for textarea */
}
.vfm {
  z-index: 999999999 !important;
}
.warranty__policies--checkbox {
  .warranty__checkmark {
    height: 17px;
    width: auto;
    top: 0.2rem;
    left: 0;
    border-radius: 5px;

    &::after {
      content: "";
      @apply absolute hidden;
      top: 3px;
      left: 6px;
      width: 5px;
      height: 10px;
      border: solid white;
      border-width: 0 3px 3px 0;
      -webkit-transform: rotate(45deg);
      -ms-transform: rotate(45deg);
      transform: rotate(45deg);
    }
  }
}
</style>
